<script setup lang="ts">
import { ref, watchEffect } from 'vue';
import { useI18n } from 'vue-i18n';
import usePrice from '@core/composables/use-price';
import { getImgCdnServiceInstance } from '@bc/shared/util';
import Product from '@monolith/legacy/types/product';

import { ProductBuilder } from '../services/product-builder';
import { isTileSimplified } from '../services/tile-simplification';
import { CardProductEmits } from '../types/card-product-emits';
import CardProduct from './card-product.vue';

const props = defineProps<{
  product: Product;
  pageNumber: number;
  position: number;
  isUserAuthenticated: boolean;
  canGetWholesalePrice: boolean;
}>();
defineEmits<CardProductEmits>();

const { getConvertedPrice } = usePrice();
const i18n = useI18n();
const productViewModel = ref<Awaited<ReturnType<ProductBuilder['build']>>>();

watchEffect(async () => {
  productViewModel.value = await new ProductBuilder(getImgCdnServiceInstance(), i18n, getConvertedPrice)
    .with('id', props.product.id)
    .with('name', props.product.name as string)
    .with('images', props.product.images)
    .with('link', props.product.link)
    .with('variants', props.product.variants)
    .with('brand', props.product.brand)
    .with('brandName', props.product.brand.name)
    .with('brandLink', props.product.brand.link)
    .with('discountPercentage', props.product.discount_rate)
    .with('simplified', isTileSimplified())
    .with('unitMultiplier', props.product.unit_multiplier)
    .with('tags', props.product.tags?.[i18n.locale.value] ?? props.product.tags)
    .with('isNew', props.product.is_new)
    .with('wholeSalePrice', props.product.wholesale_price)
    .with('originalWholeSalePrice', props.product.original_wholesale_price)
    .with('retailPrice', props.product.retail_price)
    .with('available_at', props.product.available_at)
    .build();
});
</script>

<template>
  <CardProduct
    v-if="productViewModel"
    :product="productViewModel"
    :qtac-product="product"
    :page-number="pageNumber"
    :position="position"
    :can-get-wholesale-price="canGetWholesalePrice"
    :is-user-authenticated="isUserAuthenticated"
    @clicked="$emit('clicked', $event)"
    @brand-clicked="$emit('brandClicked', $event)"
    @add-to-cart-clicked="$emit('addToCartClicked', $event)"
    @added-to-cart="$emit('addedToCart', $event)"
    @like-clicked="$emit('likeClicked', $event)"
    @unlike-clicked="$emit('unlikeClicked', $event)"
  />
</template>
